/**
 * apps/battle/client/src/terminal/screens/PortalExitScreen.tsx
 * Portal exit narrative — shown after a passing portal answer (first deploy).
 */
import { useEffect, useState } from 'react';
import { TerminalFrame } from '../components/TerminalFrame.js';
import { KeyBar, type KeyBarItem } from '../components/KeyBar.js';
import { useKeyboard } from '../hooks/useKeyboard.js';
import { PORTAL_NARRATIVE, type PortalKey } from '../narrative.js';
import { NARRATIVE_AUTO_ADVANCE_MS } from '../config.js';
import type { PortalState } from '../../api.js';

interface Props {
  portal: PortalState;
  xpAwarded: number;
  onContinue: () => void;
}

export function PortalExitScreen({ portal, xpAwarded, onContinue }: Props) {
  const n = PORTAL_NARRATIVE[portal.portalKey as PortalKey];
  const [countdown, setCountdown] = useState(Math.round(NARRATIVE_AUTO_ADVANCE_MS / 1000));

  useEffect(() => {
    const t = setTimeout(onContinue, NARRATIVE_AUTO_ADVANCE_MS);
    const i = setInterval(() => setCountdown((c) => Math.max(0, c - 1)), 1000);
    return () => { clearTimeout(t); clearInterval(i); };
  }, [onContinue]);

  useKeyboard([{ key: 'Enter', handler: onContinue }]);

  const title = (
    <>
      ECONQUEST <span className="tm-text--dim">{'\u2500'}</span>{' '}
      <span className="tm-text--portal">PORTAL {portal.endpointA} {'\u2194'} {portal.endpointB}</span>{' '}
      <span className="tm-text--dim">{'\u2500'}</span>{' '}
      <span className="tm-text--accent">DEPLOYED</span>
    </>
  );

  const keybar: KeyBarItem[] = [
    { label: 'Enter', description: 'continue', onActivate: onContinue },
  ];

  return (
    <TerminalFrame title={title} keybar={<KeyBar items={keybar} />}>
      <div className="tm-narrative">
        <div className="tm-h1 tm-text--portal">
          {'\u26A1'} {n?.name ?? `${portal.endpointA} \u2194 ${portal.endpointB}`}
        </div>
        {n?.exit ?? 'The bridge holds. You may cross again whenever needed.'}
        {xpAwarded > 0 && (
          <div className="tm-narrative__unlock">+{xpAwarded} XP earned.</div>
        )}
        <div className="tm-narrative__sub">
          {'>'} auto-advance in {countdown}s
        </div>
      </div>
    </TerminalFrame>
  );
}
